// The below tables are used for sort order

import {
    CheckInFormType,
    CheckInStatus,
    CheckInType,
    OrgwikiCheckInStateChoices,
    OrgwikiCheckInTypeChoices,
} from '~/generated/types'
import moment from 'moment'
import { CheckIn, CheckInEmployee } from '~/app/types'
import { EmployeesModel } from '~/models/EmployeesModel'

const statusSortOrder = [CheckInStatus.Green, CheckInStatus.Yellow, CheckInStatus.Red]

const typeSortOrder = [
    CheckInFormType.AdHoc,
    CheckInFormType.Annual,
    CheckInFormType.FollowUp,
    CheckInFormType.NewEmployee_1Month,
    CheckInFormType.NewEmployee_3Months,
    CheckInFormType.NewEmployee_6Months,
]

export const sortByDate = (a: CheckIn, b: CheckIn) => {
    const isADue =
        a.state === OrgwikiCheckInStateChoices.CheckInDue || a.state === OrgwikiCheckInStateChoices.CommentsDue
    const isBDue =
        b.state === OrgwikiCheckInStateChoices.CheckInDue || b.state === OrgwikiCheckInStateChoices.CommentsDue
    const dateA = isADue
        ? a.state == OrgwikiCheckInStateChoices.CheckInDue
            ? a.dueDate
            : a.employeeCommentDueDate
        : a.displayDate
    const dateB = isBDue
        ? b.state == OrgwikiCheckInStateChoices.CheckInDue
            ? b.dueDate
            : b.employeeCommentDueDate
        : b.displayDate

    return moment(dateA).isBefore(moment(dateB)) ? -1 : 1
}

export const dateSort = (a: CheckIn, b: CheckIn) => {
    const isADue = a.state == OrgwikiCheckInStateChoices.CheckInDue || a.state == OrgwikiCheckInStateChoices.CommentsDue
    const isBDue = b.state == OrgwikiCheckInStateChoices.CheckInDue || b.state == OrgwikiCheckInStateChoices.CommentsDue

    // Should use the last modified time if it is a completed check in
    let dueDateA = a.modifiedDatetime
    let dueDateB = b.modifiedDatetime

    if (isADue) {
        dueDateA = a.state == OrgwikiCheckInStateChoices.CheckInDue ? a.dueDate : a.employeeCommentDueDate
    }
    if (isBDue) {
        dueDateB = b.state == OrgwikiCheckInStateChoices.CheckInDue ? b.dueDate : b.employeeCommentDueDate
    }
    const isAOverDue = moment(isADue ? dueDateA : a.dueDate).isBefore(moment())
    const isBOverDue = moment(isBDue ? dueDateB : b.dueDate).isBefore(moment())

    if (isADue !== isBDue) {
        return isADue ? 1 : -1
    } else if (isADue && isBDue && isAOverDue !== isBOverDue) {
        return isAOverDue ? 1 : -1
    } else if (dueDateA !== dueDateB) {
        return moment(dueDateA).isBefore(moment(dueDateB)) ? -1 : 1
    }

    return defaultSort(b, a)
}

export const typeSort = (a: CheckIn, b: CheckIn) => {
    if (a.type !== b.type) return typeSortOrder.indexOf(b.type) - typeSortOrder.indexOf(a.type)

    return defaultSort(b, a)
}

export const nameCompareSort = (a: string, b: string, dateA: CheckIn, dateB: CheckIn) => {
    if (a !== b) return a.localeCompare(b)

    return sortByDate(dateA, dateB)
}

export const employeeNamesSort = (a: CheckIn, b: CheckIn) => {
    const employeeA = EmployeesModel.getEmployee(a.employee.employeeId)
    const employeeB = EmployeesModel.getEmployee(b.employee.employeeId)
    if (!employeeA || !employeeB) return 0
    return nameCompareSort(employeeA.full_name, employeeB.full_name, a, b)
}

export const managerNamesSort = (a: CheckIn, b: CheckIn) => {
    if (!a.manager || !b.manager) return 0
    const employeeA = EmployeesModel.getEmployee(a.manager.employeeId)
    const employeeB = EmployeesModel.getEmployee(b.manager.employeeId)
    if (!employeeA || !employeeB) return 0
    return nameCompareSort(employeeA.full_name, employeeB.full_name, a, b)
}

export const defaultSort = employeeNamesSort

export const statusSort = (a: CheckIn, b: CheckIn) => {
    if (b.status !== a.status)
        return (
            statusSortOrder.indexOf(b.status ?? CheckInStatus.Red) -
            statusSortOrder.indexOf(a.status ?? CheckInStatus.Red)
        )

    return defaultSort(b, a)
}

export const engagementSort = (a: CheckIn, b: CheckIn) => {
    if (b.engagement !== a.engagement)
        return (
            statusSortOrder.indexOf(b.engagement ?? CheckInStatus.Red) -
            statusSortOrder.indexOf(a.engagement ?? CheckInStatus.Red)
        )

    return defaultSort(b, a)
}

export const isInManagementChain = (employeeLineage: string, employeeId: number) => {
    return employeeLineage.split('-').includes(String(employeeId))
}

export const asCheckInType = (type: OrgwikiCheckInTypeChoices) => {
    switch (type) {
        case OrgwikiCheckInTypeChoices.AdHoc:
            return CheckInFormType.AdHoc
        case OrgwikiCheckInTypeChoices.Annual:
            return CheckInFormType.Annual
        case OrgwikiCheckInTypeChoices.FollowUp:
            return CheckInFormType.FollowUp
        case OrgwikiCheckInTypeChoices.NewEmployee_1Month:
            return CheckInFormType.NewEmployee_1Month
        case OrgwikiCheckInTypeChoices.NewEmployee_3Months:
            return CheckInFormType.NewEmployee_3Months
        case OrgwikiCheckInTypeChoices.NewEmployee_6Months:
            return CheckInFormType.NewEmployee_6Months
    }
}

export const asCheckIn = (checkIn: CheckInType) => {
    const employee = {
        id: checkIn.employee.id,
        employeeId: checkIn.employee.employeeId,
    } as CheckInEmployee

    const manager = {
        id: checkIn.managerId,
        employeeId: checkIn.managerId,
    } as CheckInEmployee

    return {
        checkInId: checkIn.checkInId.toString(),
        createdDatetime: checkIn.createdDatetime,
        displayDate: checkIn.displayDate,
        dueDate: checkIn.dueDate,
        employee: employee,
        employeeComment: checkIn.employeeComment,
        employeeCommentDueDate: checkIn.employeeCommentDueDate,
        employeeTitle: checkIn.employeeTitle,
        engagement: checkIn.engagement,
        format: checkIn.format,
        id: checkIn.id,
        manager: manager,
        managerComment: checkIn.managerComment,
        managerTitle: checkIn.managerTitle,
        modifiedDatetime: checkIn.modifiedDatetime,
        reasonNotRequired: checkIn.reasonNotRequired,
        state: checkIn.state,
        status: checkIn.status,
        type: asCheckInType(checkIn.type),
    } as CheckIn
}
